import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, UserPlus } from "lucide-react";
import { AuthShell, GoogleButton, inputCls } from "@/components/AuthShell";
import { formatApiErrorDetail, useAuth } from "@/context/AuthContext";
import { useSEO } from "@/components/SEO";

export default function SignupPage() {
  useSEO({ title: "Create Account — Loan Salaah", description: "Create a free Loan Salaah account to track your loan and credit card applications." });
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/account", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await register(form.name, form.email, form.password);
      navigate("/account", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AuthShell
      eyebrow="Create your account"
      title="Your applications, always in sight."
      subtitle="One free account to follow every loan and credit card application you make through Loan Salaah."
      footer={<>Already have an account? <Link to="/login" data-testid="signup-login-link" className="font-semibold text-concordia underline decoration-brass/50 underline-offset-2">Sign in</Link></>}
    >
      <form data-testid="signup-form" onSubmit={submit} className="mt-8 space-y-5" noValidate>
        <div>
          <label htmlFor="signup-name-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Full name</label>
          <input id="signup-name-input" data-testid="signup-name-input" className={inputCls} placeholder="e.g. Priya Sharma"
            value={form.name} onChange={set("name")} />
        </div>
        <div>
          <label htmlFor="signup-email-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Email</label>
          <input id="signup-email-input" data-testid="signup-email-input" type="email" className={inputCls} placeholder="you@example.com"
            value={form.email} onChange={set("email")} />
          <p className="mt-1 text-[0.7rem] text-slate/70">Use the same email you apply with — your applications link to it automatically.</p>
        </div>
        <div>
          <label htmlFor="signup-password-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Password</label>
          <input id="signup-password-input" data-testid="signup-password-input" type="password" className={inputCls} placeholder="Minimum 8 characters"
            value={form.password} onChange={set("password")} />
        </div>
        {error && <p data-testid="signup-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">{error}</p>}
        <button type="submit" data-testid="signup-submit-button" disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-concordia px-8 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-concordia-deep disabled:cursor-not-allowed disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          {busy ? "Creating…" : "Create account"}
        </button>
        <div className="flex items-center gap-4 py-1">
          <span className="h-px flex-1 bg-sand" />
          <span className="text-[0.65rem] uppercase tracking-[0.25em] text-slate">or</span>
          <span className="h-px flex-1 bg-sand" />
        </div>
        <GoogleButton />
      </form>
    </AuthShell>
  );
}
